import {
  BOX_CATEGORY_INFO,
  type BoxCategory,
  type Currency,
  formatAmountWithUnit,
  LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS,
  LIVE_BREAK_MAX_RATE_BPS,
  LIVE_BREAK_SETTINGS_DEFAULTS,
  type LiveBreakHostingMode,
  type LiveBreakSettings,
  type LiveBreakSettingsInput,
  parseAmount,
  resolveStorefrontFeatures,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import {
  AddLiveBreakHostDialog,
  RemoveLiveBreakHostButton,
} from "../components/LiveBreakHostListControls";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { SettingRow } from "../components/SettingRow";
import { ShortDate } from "../components/ShortDate";
import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { TwitchAppCard } from "../components/TwitchAppCard";
import { UnsavedChangesDialog } from "../components/UnsavedChangesDialog";
import { amountInputLabel, amountInputStep, amountInputValue, useCurrency } from "../lib/currency";
import {
  allowerLabel,
  categoriesLeftOff,
  type LiveBreakHost,
  parseRatePercent,
  ratePercentText,
} from "../lib/liveBreaks";
import { useStorefrontConfig, useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { useUnsavedChangesGuard } from "../lib/useUnsavedChangesGuard";

/**
 * Live breaks on one storefront environment: the switch (`features.liveBreaks`, on the header,
 * saved on flip through `storefront.update` as its one key), then the hosting and commission
 * settings, which `storefront.setLiveBreakSettings` replaces whole (admins): Community live breaks
 * first, which decides whether shoppers host their own or the store's hosts go live one live break
 * at a time; then the Host Allow List (`liveBreaks.hosts.*`, editors), which is the store's hosts
 * while community live breaks are off and decides who may host while they are on and the settings
 * enforce it; then the environment's own Twitch app (`twitchApps.*`, admins).
 */
export function StorefrontLiveBreaksPage() {
  const { can } = useTenantRole();
  const { query, update } = useStorefrontConfig();

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment } = query.data;
  const enabled = resolveStorefrontFeatures(config.features).liveBreaks;
  const settings = config.liveBreakSettings;

  return (
    <StorefrontSection
      title="Live breaks"
      description="A host opens shoppers' boxes from a queue, one entry at a time, usually while streaming, and earns a commission on top of the box price. Off refuses every live-break action except closing one and claiming what it revealed or earned."
      environment={environment}
      aside={
        <SectionSwitch
          id="live-breaks-enabled"
          label="Live breaks"
          checked={enabled}
          disabled={update.isPending || !can("editor")}
          onCheckedChange={(checked) => update.mutate({ features: { liveBreaks: checked } })}
        />
      }
    >
      <HostingCard settings={settings} canEdit={can("admin")} />
      <CategoriesCard offered={config.boxCategories} />
      <HostAllowListCard
        hosting={settings.hosting}
        enforced={settings.hostAllowlistEnforced}
        canEdit={can("editor")}
      />
      <TwitchAppCard canEdit={can("admin")} />
    </StorefrontSection>
  );
}

// ---- hosting and commission ---------------------------------------------------------------------

const HOST_POLICY_LABELS = {
  anyone: "Anyone signed in",
  allowlist: "Only the Host Allow List",
} as const;
type HostPolicy = keyof typeof HOST_POLICY_LABELS;
const HOST_POLICIES = Object.keys(HOST_POLICY_LABELS) as HostPolicy[];

/** The settings as the form holds them: rates and the price as typed. */
interface SettingsDraft {
  hosting: LiveBreakHostingMode;
  hostAllowlistEnforced: boolean;
  defaultRate: string;
  maxRate: string;
  minBoxPrice: string;
}

function draftOf(settings: LiveBreakSettings, currency: Currency): SettingsDraft {
  return {
    hosting: settings.hosting,
    hostAllowlistEnforced: settings.hostAllowlistEnforced,
    defaultRate: ratePercentText(settings.defaultRateBps),
    maxRate: ratePercentText(settings.maxRateBps),
    minBoxPrice: amountInputValue(settings.defaultMinBoxPriceCents, currency),
  };
}

/** Whether a save would change nothing the server holds. */
function sameSettings(input: LiveBreakSettingsInput, settings: LiveBreakSettings): boolean {
  return (
    input.hosting === settings.hosting &&
    input.hostAllowlistEnforced === settings.hostAllowlistEnforced &&
    input.defaultRateBps === settings.defaultRateBps &&
    input.maxRateBps === settings.maxRateBps &&
    input.defaultMinBoxPriceCents === settings.defaultMinBoxPriceCents
  );
}

/** `setLiveBreakSettings`, with the cache refreshed from what the server saved. */
function useSaveLiveBreakSettings(onSaved: () => void) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { queryOptions } = useStorefrontConfigQuery();
  return useMutation(
    trpc.storefront.setLiveBreakSettings.mutationOptions({
      onSuccess: ({ settings }) => {
        queryClient.setQueryData(queryOptions.queryKey, (old) =>
          old ? { ...old, config: { ...old.config, liveBreakSettings: settings } } : old,
        );
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        onSaved();
        toast.success("Live break settings saved.");
      },
    }),
  );
}

/**
 * Whether community live breaks are on, who may host while they are, the commission rates and the
 * minimum box price a new live break starts with; the categories live breaks sell from are the
 * storefront's own (`CategoriesCard`).
 * With community live breaks off the store's hosts go live, so who may host isn't asked. A draft
 * until saved; the save sends it whole, and leaving with unsaved changes asks first. Admins edit;
 * everyone else reads.
 */
export function HostingCard({
  settings,
  canEdit,
}: {
  settings: LiveBreakSettings;
  canEdit: boolean;
}) {
  const currency = useCurrency();
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const save = useSaveLiveBreakSettings(() => setDraft(null));

  const current = draft ?? draftOf(settings, currency);
  const edit = (patch: Partial<SettingsDraft>) => setDraft({ ...current, ...patch });
  const locked = !canEdit || save.isPending;

  const maxRateBps = parseRatePercent(current.maxRate);
  // A 0% maximum turns commission off, and the default can only follow it down.
  const commissionOff = maxRateBps === 0;
  const defaultRateBps = commissionOff ? 0 : parseRatePercent(current.defaultRate);
  const defaultAboveMax =
    defaultRateBps !== null && maxRateBps !== null && defaultRateBps > maxRateBps;
  const minBoxPriceCents = parseAmount(current.minBoxPrice, currency);
  const minBoxPriceValid =
    minBoxPriceCents !== null &&
    minBoxPriceCents >= 0 &&
    minBoxPriceCents <= LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS;

  const input: LiveBreakSettingsInput | null =
    maxRateBps !== null && defaultRateBps !== null && !defaultAboveMax && minBoxPriceValid
      ? {
          hosting: current.hosting,
          hostAllowlistEnforced: current.hostAllowlistEnforced,
          defaultRateBps,
          maxRateBps,
          defaultMinBoxPriceCents: minBoxPriceCents,
        }
      : null;
  const dirty = draft !== null && (input === null || !sameSettings(input, settings));
  const ceiling = ratePercentText(LIVE_BREAK_MAX_RATE_BPS);
  const guard = useUnsavedChangesGuard({
    dirty: canEdit && dirty,
    // Save and leave only for a draft that saves; one with an issue can only be kept or dropped.
    onSave: input ? () => save.mutateAsync({ settings: input }) : undefined,
    onDiscard: () => setDraft(null),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input) save.mutate({ settings: input });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hosting and commission</CardTitle>
        <CardDescription>
          A host's commission is added on top of the box price: a shopper buying into their live
          break pays the box price plus the host's rate, and the box itself is priced as it is
          everywhere else. A host starts at the default rate and never earns more than the maximum.
          Live breaks already open keep the rate and minimum box price they started with.
        </CardDescription>
        {canEdit && (
          <CardAction>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={save.isPending}
              onClick={() => setDraft(draftOf(LIVE_BREAK_SETTINGS_DEFAULTS, currency))}
            >
              Use defaults
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <UnsavedChangesDialog guard={guard} />
        <form className="grid gap-5" onSubmit={submit}>
          <div className="grid divide-y">
            <SettingRow
              htmlFor="live-break-community"
              label="Community live breaks"
              description="On: shoppers host live breaks of their own, many at once, and the storefront lists them to pick from. Off: only your store's hosts go live, one live break at a time, and the storefront shows that live break or says your store isn't live."
            >
              <Switch
                id="live-break-community"
                checked={current.hosting === "community"}
                disabled={locked}
                onCheckedChange={(checked) => edit({ hosting: checked ? "community" : "store" })}
              />
            </SettingRow>
            {current.hosting === "community" && (
              <SettingRow
                htmlFor="live-break-host-policy"
                label="Who may host"
                description="Anyone signed in may start a live break, or only the shoppers on the Host Allow List below."
              >
                <Select
                  items={HOST_POLICY_LABELS}
                  value={current.hostAllowlistEnforced ? "allowlist" : "anyone"}
                  disabled={locked}
                  onValueChange={(value) => edit({ hostAllowlistEnforced: value === "allowlist" })}
                >
                  <SelectTrigger id="live-break-host-policy" className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOST_POLICIES.map((policy) => (
                      <SelectItem key={policy} value={policy}>
                        {HOST_POLICY_LABELS[policy]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SettingRow>
            )}
            <SettingRow
              htmlFor="live-break-max-rate"
              label="Maximum host rate (%)"
              description="The most any host may charge, whatever rate they or staff set. 0% turns host commission off: boxes sell at their own price and hosts earn nothing."
            >
              <RateInput
                id="live-break-max-rate"
                value={current.maxRate}
                disabled={locked}
                invalid={maxRateBps === null}
                onChange={(maxRate) => edit({ maxRate })}
                hint={`0 to ${ceiling}, two decimals at most.`}
              />
            </SettingRow>
            <SettingRow
              htmlFor="live-break-default-rate"
              label="Default host rate (%)"
              description="The rate a host starts at until they or staff set one of their own."
            >
              <RateInput
                id="live-break-default-rate"
                value={commissionOff ? ratePercentText(0) : current.defaultRate}
                disabled={locked || commissionOff}
                invalid={defaultRateBps === null || defaultAboveMax}
                onChange={(defaultRate) => edit({ defaultRate })}
                hint={
                  commissionOff
                    ? "Commission is off, so every host's rate is 0%."
                    : defaultAboveMax
                      ? `Can't be above the maximum rate, ${ratePercentText(maxRateBps ?? 0)}%.`
                      : "0 to the maximum rate, two decimals at most."
                }
              />
            </SettingRow>
            <SettingRow
              htmlFor="live-break-min-box-price"
              label={amountInputLabel("Default minimum box price", currency)}
              description="The minimum box price a new live break starts with, until its host or staff change it. A box must be at or above a live break's minimum to be added to it or bought from outside its own boxes; a box already chosen keeps selling when the minimum is raised."
            >
              <div className="grid max-w-xs gap-2">
                <Input
                  id="live-break-min-box-price"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={amountInputStep(currency)}
                  value={current.minBoxPrice}
                  disabled={locked}
                  onChange={(e) => edit({ minBoxPrice: e.target.value })}
                  aria-invalid={!minBoxPriceValid}
                />
                <p
                  className={`text-xs ${minBoxPriceValid ? "text-muted-foreground" : "text-destructive"}`}
                >
                  {`0 for no minimum, at most ${formatAmountWithUnit(LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS, currency)}.`}
                </p>
              </div>
            </SettingRow>
          </div>
          {canEdit ? (
            <div className="flex gap-2">
              <Button type="submit" disabled={!dirty || input === null || save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              {draft !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={save.isPending}
                  onClick={() => setDraft(null)}
                >
                  Discard
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Only admins can change these settings.</p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/** A percent field over basis points, with its hint (or what is wrong with it) beneath. */
function RateInput({
  id,
  value,
  disabled,
  invalid,
  hint,
  onChange,
}: {
  id: string;
  value: string;
  disabled: boolean;
  invalid: boolean;
  hint: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid max-w-xs gap-2">
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        max={LIVE_BREAK_MAX_RATE_BPS / 100}
        step={0.01}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={invalid}
      />
      <p className={`text-xs ${invalid ? "text-destructive" : "text-muted-foreground"}`}>{hint}</p>
    </div>
  );
}

/**
 * The box categories live breaks sell from: the storefront's own (Storefront → Categories), the
 * same switches as the boxes page and the forge, so this card only reports them and links there.
 * A box in a category left off can't be added to a live break or bought in one.
 */
function CategoriesCard({ offered }: { offered: readonly BoxCategory[] }) {
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId/storefront/live-breaks" });
  const off = categoriesLeftOff(offered);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Categories</CardTitle>
        <CardDescription>
          Live breaks sell boxes from the categories your store offers, the same switches as the
          boxes page and the forge. A box in a category that's off can't be added to a live break or
          bought in one.
        </CardDescription>
        <CardAction>
          <Link
            to="/tenants/$tenantId/storefront/categories"
            params={{ tenantId }}
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            Edit categories
          </Link>
        </CardAction>
      </CardHeader>
      <CardContent>
        <p className="text-sm">
          {off.length === 0
            ? "Every category is on."
            : `Off: ${off.map((code) => BOX_CATEGORY_INFO[code].label).join(", ")}.`}
        </p>
      </CardContent>
    </Card>
  );
}

// ---- Host Allow List ----------------------------------------------------------------------------

const hostColumns = createDataTableColumns<LiveBreakHost>();
const HOST_PINNED = { start: [], end: ["actions"] };

function buildHostColumns(tenantId: string, canEdit: boolean) {
  const columns = [
    hostColumns.accessor((host) => host.username ?? "", {
      id: "username",
      header: "Username",
      cell: ({ getValue }) =>
        getValue() ? (
          <span className="font-medium">{getValue()}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      meta: { filter: "text" },
    }),
    hostColumns.accessor("externalId", {
      header: "External id",
      size: 220,
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/users/$userId"
          params={{ tenantId, userId: row.original.endUserId }}
          className="font-mono text-xs hover:underline"
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    hostColumns.accessor((host) => host.email ?? "", {
      id: "email",
      header: "Email",
      cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
      meta: { filter: "text" },
    }),
    hostColumns.accessor("allowedAt", {
      header: "Allowed",
      cell: ({ getValue }) => {
        const allowedAt = getValue();
        return allowedAt ? <ShortDate value={allowedAt} /> : "—";
      },
      meta: { csv: (value) => (value ? String(value) : "") },
    }),
    // The tenant member who added them by name; a platform admin as platform staff. Null once that
    // user is deleted, or for a row that was never on the list.
    hostColumns.accessor((host) => allowerLabel(host.allowedBy), {
      id: "allowedBy",
      header: "Allowed by",
      cell: ({ getValue }) =>
        getValue() ? <span>{getValue()}</span> : <span className="text-muted-foreground">—</span>,
      meta: { filter: "text" },
    }),
  ];
  if (!canEdit) return hostColumns.columns(columns);
  return hostColumns.columns([
    ...columns,
    hostColumns.display({
      id: "actions",
      size: 96,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <RemoveLiveBreakHostButton host={row.original} />
        </div>
      ),
    }),
  ]);
}

/**
 * The Host Allow List: the store's hosts while community live breaks are off (the accounts that go
 * live for the store), and who may host while they are on and the settings enforce it. Editors add
 * a shopper from a search of the environment's end users and remove one behind a confirmation.
 * The title and badge read the saved settings, not the form's draft above.
 */
function HostAllowListCard({
  hosting,
  enforced,
  canEdit,
}: {
  hosting: LiveBreakHostingMode;
  enforced: boolean;
  canEdit: boolean;
}) {
  const trpc = useTRPC();
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId/storefront/live-breaks" });
  const hosts = useInfiniteQuery(
    trpc.liveBreaks.hosts.list.infiniteQueryOptions(
      { filter: "allowed", limit: 50 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = useMemo(() => hosts.data?.pages.flatMap((page) => page.hosts) ?? [], [hosts.data]);
  const allowedIds = useMemo(() => new Set(rows.map((host) => host.endUserId)), [rows]);
  const columns = useMemo(() => buildHostColumns(tenantId, canEdit), [tenantId, canEdit]);

  return (
    <Card>
      <CardHeader>
        {hosting === "store" ? (
          <>
            <CardTitle>Store hosts</CardTitle>
            <CardDescription>
              The accounts that go live for your store, one live break at a time: the Host Allow
              List. Only they can start a live break. Removing one stops their next live break, not
              one they have open.
            </CardDescription>
          </>
        ) : (
          <>
            <CardTitle className="flex items-center gap-2">
              Host Allow List
              {enforced ? <Badge>Enforced</Badge> : <Badge variant="outline">Not enforced</Badge>}
            </CardTitle>
            <CardDescription>
              {enforced
                ? "Only the shoppers on this list can start a live break. Removing one stops their next live break, not one they have open."
                : "Anyone signed in can host right now, so this list only matters once an admin sets who may host to “Only the Host Allow List” above. You can build it ahead of time."}
            </CardDescription>
          </>
        )}
        {canEdit && (
          <CardAction>
            <AddLiveBreakHostDialog allowedIds={allowedIds} />
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {hosts.error ? (
          <ErrorAlert message={hosts.error.message} />
        ) : hosts.isPending ? (
          <Skeleton className="h-40" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(host) => host.endUserId}
            pinned={canEdit ? HOST_PINNED : undefined}
            search="Search hosts"
            csvName="host-allow-list"
            emptyMessage={
              hosting === "store"
                ? canEdit
                  ? "Your store has no hosts yet. Add one to let them go live for your store."
                  : "Your store has no hosts yet."
                : canEdit
                  ? "No one is on the list yet. Add a host to let them start live breaks."
                  : "No one is on the list yet."
            }
          />
        )}
        <LoadMoreButton
          hasNextPage={hosts.hasNextPage}
          isFetchingNextPage={hosts.isFetchingNextPage}
          onClick={() => void hosts.fetchNextPage()}
        />
      </CardContent>
    </Card>
  );
}
